import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    AppBar,
    CircularProgress,
    Box,
    Drawer,
    List,
    Toolbar,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
    Snackbar,
    Alert
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BarChartIcon from '@mui/icons-material/BarChart';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import NavLinkItem from './NavLinkItem';
import { useSendLogoutMutation } from '../features/authentication/authApiSlice';
import { selectCurrentUser } from '../features/authentication/authSlice';
import { useAddNewLokiMutation } from '../api/lokiSlice';
import { roles } from '../config/roles';
import Add from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import BugReportIcon from '@mui/icons-material/BugReport';

const Navbar = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const toggleDrawer = () => setDrawerOpen((prev) => !prev);

    const { role, name } = useSelector(selectCurrentUser) || {};
    const navigate = useNavigate();
    const theme = useTheme();

    // Switch to hamburger menu below 'lg' (1200px)
    const isBelowLargeScreen = useMediaQuery(theme.breakpoints.down('lg')); // < 1200px

    const [sendLogout, { isLoading }] = useSendLogoutMutation();
    const [sendLog] = useAddNewLokiMutation();

    const handleLogout = async () => {
        try {
            await sendLog({ name, action: 'kirjautui ulos' });
            await sendLogout();
            navigate('/login');
        } catch (e) {
            if (e.message === 'Sessio on päivääntynyt') {
                setSessionExpired(true);
                setTimeout(() => navigate('/login'), 2000);
            }
        }
    };

    const navLinks = [
        { page: '', icon: <HomeIcon />, text: 'Etusivu' },
        { page: 'tapahtumat', icon: <EventNoteIcon />, text: 'Tapahtumat' },
        { page: 'bugiraportointi', icon: <BugReportIcon />, text: 'Bugiraportointi' },
        ...(role !== roles.kayttaja ? [{ page: 'tilastot', icon: <BarChartIcon />, text: 'Tilastot' }] : []),
        ...(role !== roles.kayttaja && role !== roles.laskuttaja
            ? [{ page: 'tapahtumat/uusi', icon: <Add />, text: 'Uusi tapahtuma' }]
            : []),
        ...(role === roles.admin
            ? [{ page: 'admin', icon: <SupervisorAccountIcon />, text: 'Admin' }]
            : [{ page: 'profiili', icon: <SupervisorAccountIcon />, text: 'Profiili' }]),
    ];

    // Close drawer when screen size changes to a larger breakpoint
    useEffect(() => {
        if (!isBelowLargeScreen) {
            setDrawerOpen(false);
        }
    }, [isBelowLargeScreen]);

    return (
        <>
            <AppBar position="static" color="taphaGreen" component="nav" sx={{ boxShadow: 3 }}>
                <Toolbar
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: { xs: 1, sm: 2, md: 2 },
                        py: { xs: 0.5, sm: 0.75 },
                        minHeight: { xs: 48, sm: 56 },
                    }}
                >
                    {/* Logo */}
                    <Box sx={{ flexShrink: 0 }}>
                        <Link to="/">
                            <IconButton size="large" edge="start" aria-label="logo">
                                <Box
                                    component="img"
                                    sx={{ maxWidth: { xs: 90, sm: 120, md: 150 }, height: 'auto' }}
                                    src="/SROS-logo-valkoinen.png"
                                />
                            </IconButton>
                        </Link>
                    </Box>

                    {/* Navigation */}
                    {!isBelowLargeScreen ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexGrow: 1,
                                justifyContent: 'flex-end',
                                gap: { lg: 1.5 },
                            }}
                        >
                            <List
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'nowrap',
                                    gap: { lg: 1.5 },
                                    alignItems: 'center',
                                    overflow: 'visible',
                                }}
                            >
                                {navLinks.map((link) => (
                                    <NavLinkItem
                                        key={link.page}
                                        to={`/${link.page}`}
                                        text={link.text}
                                        icon={link.icon}
                                        sx={{
                                            fontSize: { lg: '1rem' },
                                            px: { lg: 1.25 },
                                            py: 0.25,
                                            color: 'white',
                                            whiteSpace: 'nowrap',
                                            '& .MuiSvgIcon-root': { fontSize: { lg: '1.5rem' } },
                                        }}
                                    />
                                ))}
                            </List>
                            <IconButton
                                onClick={handleLogout}
                                sx={{
                                    color: 'white',
                                    px: { lg: 1 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    textDecoration: 'underline',
                                    '&:hover': { backgroundColor: 'inherit' },
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={14} sx={{ color: 'white' }} />
                                ) : (
                                    <LogoutIcon sx={{ fontSize: { lg: '1.5rem' } }} />
                                )}
                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: { lg: '1rem' }, fontFamily: '"Bree Serif", serif' }}
                                >
                                    Kirjaudu ulos
                                </Typography>
                            </IconButton>
                        </Box>
                    ) : (
                        <IconButton
                            onClick={toggleDrawer}
                            sx={{ color: 'white' }}
                            aria-label="menu"
                        >
                            <MenuIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>

            {/* Drawer for Hamburger Menu */}
            <Drawer
                open={drawerOpen}
                onClose={toggleDrawer}
                variant="temporary"
                sx={{
                    '& .MuiDrawer-paper': {
                        width: { xs: '70%', sm: '40%' },
                        maxWidth: 280,
                        backgroundColor: 'taphaGreen.main',
                        color: 'white',
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton onClick={toggleDrawer} sx={{ color: 'white' }} aria-label="close drawer">
                            <CloseIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, px: 2 }}>
                        <List sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5 } }}>
                            {navLinks.map((link) => (
                                <NavLinkItem
                                    key={link.page}
                                    to={`/${link.page}`}
                                    text={link.text}
                                    icon={link.icon}
                                    sx={{
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        py: 0.5,
                                        color: 'white',
                                        '& .MuiSvgIcon-root': { fontSize: { xs: '1.25rem', sm: '1.5rem' } },
                                    }}
                                    onClick={toggleDrawer}
                                />
                            ))}
                            <NavLinkItem
                                to="/login"
                                text="Kirjaudu ulos"
                                icon={
                                    isLoading ? (
                                        <CircularProgress size={16} sx={{ color: 'white' }} />
                                    ) : (
                                        <LogoutIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                                    )
                                }
                                sx={{
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    py: 0.5,
                                    color: 'white',
                                }}
                                onClick={handleLogout}
                            />
                        </List>
                    </Box>
                </Box>
            </Drawer>

            {/* Session Expired Snackbar */}
            <Snackbar
                open={sessionExpired}
                autoHideDuration={2000}
                onClose={() => setSessionExpired(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" onClose={() => setSessionExpired(false)}>
                    Sessio on päivääntynyt
                </Alert>
            </Snackbar>
        </>
    );
};

export default Navbar;